import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	IsArray,
	IsIn,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	Min,
	ValidateNested,
} from 'class-validator';
import { IsBoolean, ParseJson } from '../../decorators';

const SORT_DIRECTION = ['ASC', 'DESC'] as const;

class DateRangeFilter {
	@IsString()
	@IsOptional()
	public startDate?: string | null;

	@IsString()
	@IsOptional()
	public endDate?: string | null;
}

class ItemFilters {
	@IsString()
	@IsOptional()
	public search?: string;

	@IsString()
	@IsOptional()
	public itemSearch?: string;

	@IsArray()
	@IsString({ each: true })
	@IsOptional()
	public itemCategories?: Array<string>;

	@IsArray()
	@IsString({ each: true })
	@IsOptional()
	public itemManufacturers?: Array<string>;

	@IsArray()
	@IsInt({ each: true })
	@IsOptional()
	public itemCompany?: Array<number>;

	@IsArray()
	@IsInt({ each: true })
	@IsOptional()
	public itemWarehouse?: Array<number>;

	@Type(() => Number)
	@IsInt()
	@IsOptional()
	public warehouseId?: number;

	@Type(() => Number)
	@IsInt()
	@IsOptional()
	public locationId?: number;

	@IsString()
	@IsOptional()
	public itemStatus?: string;

	@IsString()
	@IsOptional()
	public itemShortDate?: string;

	// PD-15021 — undeclared filter keys are stripped by the validation
	// whitelist, so the list's Non-Inventory chip is inert without this.
	@IsString()
	@IsOptional()
	public itemNonInventory?: string;

	@IsString()
	@IsOptional()
	public stock_status?: string;

	// Product-variant model: 'All' | 'standalone' | 'product' | 'variant'.
	@IsString()
	@IsOptional()
	public itemLevel?: string;

	@IsArray()
	@IsInt({ each: true })
	@IsOptional()
	public itemGroups?: Array<number>;

	@IsArray()
	@IsInt({ each: true })
	@IsOptional()
	public excludeItemGroups?: Array<number>;

	// PD-15839 — hand-picked item lists. Declared here for the same reason as
	// the price-change keys below: the validation whitelist strips undeclared
	// filter keys before the query builder sees them.
	@IsArray()
	@IsInt({ each: true })
	@IsOptional()
	public itemLists?: Array<number>;

	@IsArray()
	@IsInt({ each: true })
	@IsOptional()
	public excludeItemLists?: Array<number>;

	@ValidateNested()
	@Type(() => DateRangeFilter)
	@IsOptional()
	public itemRestockedDateRange?: DateRangeFilter;

	@ValidateNested()
	@Type(() => DateRangeFilter)
	@IsOptional()
	public itemCreatedDateRange?: DateRangeFilter;

	// PD-11124 — backs the "Price Changes" tab. Filter keys that are not
	// declared here are silently stripped by the validation whitelist before
	// the query builder ever sees them, so the list would quietly return every
	// item instead of only those whose price changed.
	@ValidateNested()
	@Type(() => DateRangeFilter)
	@IsOptional()
	public itemPriceChangedDateRange?: DateRangeFilter;

	@IsString()
	@IsOptional()
	public itemPriceChangeDirection?: string;
}

class SortFields {
	@IsString()
	public objectTable!: string;

	@IsString()
	public fieldName!: string;

	@IsIn(SORT_DIRECTION)
	public direction!: (typeof SORT_DIRECTION)[number];
}

export class GetItemsRequestDto {
	@Type(() => Number)
	@IsNumber()
	@Min(1)
	@IsOptional()
	public limit: number = 25;

	@Type(() => Number)
	@IsNumber()
	@Min(1)
	@IsOptional()
	public page: number = 1;

	@IsString()
	@IsOptional()
	public search?: string;

	@IsArray()
	@ArrayMaxSize(1000)
	@IsInt({ each: true })
	@Type(() => Number)
	@IsOptional()
	public excludeItemIds?: Array<number>;

	@IsOptional()
	@ValidateNested()
	@ParseJson({ type: ItemFilters })
	public filters?: ItemFilters;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@ParseJson({ type: SortFields })
	public sortFields?: Array<SortFields>;

	@IsBoolean()
	@IsOptional()
	public includeUnmatched: boolean = false;

	@IsBoolean()
	@IsOptional()
	public includeImages: boolean = false;

	@IsBoolean()
	@IsOptional()
	public countOnly: boolean = false;

	@IsOptional()
	@IsString()
	public cacheKey?: string;

	@Type(() => Number)
	@IsNumber()
	@IsOptional()
	public cacheTTL?: number;

	@IsOptional()
	@IsString()
	public listId?: string;

	/**
	 * Opt-in for the admin items list only (product-variants Wave 4): include
	 * `itemLevel = 'product'` rows, which every other caller must never see.
	 *
	 * Typed as a string, not a boolean, because the global ValidationPipe runs
	 * with `enableImplicitConversion` off by policy (apps/crm-api/src/main.ts),
	 * so a query param arrives as the literal 'true'. The controller compares
	 * against 'true'.
	 */
	@IsOptional()
	@IsString()
	public includeProductLevel?: string;

	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	public fields?: Array<string>;
}
